import { BINDING_SAMPLE_VALUES } from "./fhirMappingMock";
import { FHIR_MAPPINGS_CONFIG } from "./fhirMappingsConfig";
import {
  FHIR_SAMPLE_BUNDLE,
  FHIR_SAMPLE_RESOURCES,
} from "./fhirSampleBundle";

const SYSTEM_ONLY_TRANSFORMS = new Set([
  "utcNow",
  "deriveControlNumber",
  "resolvePayerIdentifier",
  "resolveFacilityIdentifierCode",
]);

function formatMappingSource(entry) {
  if (entry.fhirPath) return entry.fhirPath;
  if (entry.supportingInfoCategory) {
    return `Claim.supportingInfo[category=${entry.supportingInfoCategory}]`;
  }
  if (entry.supportingInfoCategories?.length) {
    return `Claim.supportingInfo[category=${entry.supportingInfoCategories.join(" | ")}]`;
  }
  if (entry.transform) return `transform: ${entry.transform}`;
  return null;
}

function resolveSampleValue(target) {
  if (BINDING_SAMPLE_VALUES[target] != null) {
    return BINDING_SAMPLE_VALUES[target];
  }
  const dotted = target.includes(".") ? target.split(".").pop() : target;
  if (BINDING_SAMPLE_VALUES[dotted] != null) {
    return BINDING_SAMPLE_VALUES[dotted];
  }
  return "—";
}

function buildGuideRow(target, entry) {
  const source = formatMappingSource(entry);
  return {
    binding: target,
    fhirPath: entry.fhirPath ?? null,
    mappingSource: source,
    supportingInfoCategory: entry.supportingInfoCategory ?? null,
    supportingInfoCategories: entry.supportingInfoCategories ?? null,
    fallbackPath: entry.fallbackPath ?? null,
    sampleValue: resolveSampleValue(target),
    transform: entry.transform ?? null,
    notes: entry.notes ?? null,
    isFromFhir: Boolean(
      entry.fhirPath ||
        entry.supportingInfoCategory ||
        entry.supportingInfoCategories?.length
    ),
    isSystemOnly:
      !entry.fhirPath &&
      !entry.supportingInfoCategory &&
      !entry.supportingInfoCategories?.length &&
      (SYSTEM_ONLY_TRANSFORMS.has(entry.transform) ||
        entry.transform === "utcNow" ||
        (entry.transform && entry.transform.startsWith("build"))),
  };
}

/** Build mapped-field guide from backend FhirMappings.json mirror. */
export function getMappedFieldGuide() {
  const fhirRows = [];
  const nonFhirRows = [];

  (FHIR_MAPPINGS_CONFIG.mappings ?? []).forEach((entry) => {
    const row = buildGuideRow(entry.target, entry);
    if (row.isFromFhir) fhirRows.push(row);
    else nonFhirRows.push(row);
  });

  (FHIR_MAPPINGS_CONFIG.collections ?? []).forEach((collection) => {
    (collection.itemMappings ?? []).forEach((item) => {
      const target = `${collection.target}.${item.target}`;
      const row = buildGuideRow(target, {
        ...item,
        fhirPath: item.fhirPath
          ? `Claim.${collection.target === "Services" ? "item" : collection.target.toLowerCase()}.${item.fhirPath}`
          : item.fhirPath,
        notes: item.notes ?? collection.notes,
      });
      if (row.isFromFhir) fhirRows.push(row);
      else nonFhirRows.push(row);
    });
  });

  fhirRows.sort((a, b) => a.binding.localeCompare(b.binding));
  nonFhirRows.sort((a, b) => a.binding.localeCompare(b.binding));

  return { fhirRows, nonFhirRows };
}

export function filterMappedFields(rows, query) {
  if (!query?.trim()) return rows;
  const q = query.trim().toLowerCase();
  return rows.filter(
    (row) =>
      row.binding.toLowerCase().includes(q) ||
      (row.fhirPath && row.fhirPath.toLowerCase().includes(q)) ||
      (row.mappingSource && row.mappingSource.toLowerCase().includes(q)) ||
      (row.fallbackPath && row.fallbackPath.toLowerCase().includes(q)) ||
      String(row.sampleValue).toLowerCase().includes(q) ||
      (row.transform && row.transform.toLowerCase().includes(q)) ||
      (row.notes && row.notes.toLowerCase().includes(q))
  );
}

export function formatResourceJson(resource, indent = 2) {
  return JSON.stringify(resource, null, indent);
}

export function formatBundleJson(bundle = FHIR_SAMPLE_BUNDLE) {
  return JSON.stringify(bundle, null, 2);
}

export function getResourceForTab(tabId) {
  if (tabId === "bundle") return FHIR_SAMPLE_BUNDLE;
  return FHIR_SAMPLE_RESOURCES[tabId] ?? FHIR_SAMPLE_BUNDLE;
}

/** Suggest resource tab from selected EDI segment id. */
export function suggestResourceTabFromSegment(segmentId) {
  if (!segmentId) return "bundle";
  const id = segmentId.toUpperCase();

  if (id.includes("_IL") || id.startsWith("DMG") || id.includes("PATIENT")) {
    return "Patient";
  }
  if (id.includes("_1P") || id.includes("REQUESTER")) {
    return "RequesterOrganization";
  }
  if (id.includes("_PR") || id.includes("_X3") || id.includes("PAYER")) {
    return "Organization";
  }
  if (id.startsWith("SV") || id.startsWith("HI") || id.startsWith("UM")) {
    return "Claim";
  }
  if (id.startsWith("REF") && id.includes("SUBSCRIBER")) {
    return "Coverage";
  }

  return "bundle";
}

/** Tokenize one line of pretty-printed JSON for syntax highlighting. */
export function tokenizeJsonLine(line) {
  const tokens = [];
  let i = 0;

  while (i < line.length) {
    const rest = line.slice(i);

    const wsMatch = rest.match(/^(\s+)/);
    if (wsMatch) {
      tokens.push({ text: wsMatch[1], type: "ws" });
      i += wsMatch[1].length;
      continue;
    }

    if (rest[0] === '"') {
      let end = 1;
      while (end < rest.length) {
        if (rest[end] === "\\") {
          end += 2;
          continue;
        }
        if (rest[end] === '"') {
          end += 1;
          break;
        }
        end += 1;
      }
      const chunk = rest.slice(0, end);
      const isKey = rest.slice(end).trimStart().startsWith(":");
      tokens.push({ text: chunk, type: isKey ? "key" : "string" });
      i += end;
      continue;
    }

    const numMatch = rest.match(/^(-?\d+(?:\.\d+)?)/);
    if (numMatch) {
      tokens.push({ text: numMatch[1], type: "number" });
      i += numMatch[1].length;
      continue;
    }

    if (/^(true|false|null)/.test(rest)) {
      const word = rest.match(/^(true|false|null)/)[1];
      tokens.push({ text: word, type: "literal" });
      i += word.length;
      continue;
    }

    const punctMatch = rest.match(/^([{}\[\]:,]+)/);
    if (punctMatch) {
      tokens.push({ text: punctMatch[1], type: "punct" });
      i += punctMatch[1].length;
      continue;
    }

    tokens.push({ text: rest[0], type: "text" });
    i += 1;
  }

  return tokens;
}

export function buildJsonPreviewLines(resource) {
  const text = formatResourceJson(resource);
  return text.split("\n").map((line, index) => ({
    lineNumber: index + 1,
    text: line,
    tokens: tokenizeJsonLine(line),
    matchesSearch: false,
  }));
}

export function applySearchToJsonLines(lines, query) {
  if (!query?.trim()) {
    return lines.map((line) => ({ ...line, matchesSearch: false }));
  }
  const q = query.trim().toLowerCase();
  return lines.map((line) => ({
    ...line,
    matchesSearch: line.text.toLowerCase().includes(q),
  }));
}

export { FHIR_MAPPINGS_CONFIG };
